/**
 * useGoalFetching — Low-level goal fetching with Supabase query building.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { GOALS_CHANGED_EVENT } from "@/lib/bucketList";
import { supabase, withSessionSetup, getCurrentClientUserId } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { PostgrestSingleResponse } from "@supabase/supabase-js";
import { logger } from '@/utils/logger';

export const useGoalFetching = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Refetch when goals are added outside the normal goal flow (bucket list saves, guest sync).
  useEffect(() => {
    const refresh = () => queryClient.invalidateQueries({ queryKey: ["goals"] });
    window.addEventListener(GOALS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(GOALS_CHANGED_EVENT, refresh);
  }, [queryClient]);

  const { data: goals = [], isLoading, error } = useQuery({
    queryKey: ["goals", user?.id],
    queryFn: async () => {
      if (!user) return [];

      
      
      // Get the current client user ID for data validation
      const clientUserId = await getCurrentClientUserId();
      if (!clientUserId) {
        logger.error("[useGoalFetching] No client user ID available");
        return [];
      }

      try {
        // Use the withSessionSetup helper to ensure session is properly set up
        // This ensures that RLS is working correctly with the user's session
        return await withSessionSetup(async () => {
          // Verify current user context before query
          try {
            const { data: sessionVars, error: authError } = await supabase.rpc('debug_session_variables') as PostgrestSingleResponse<any>;
              
            
            
            // Only error if there's an ACTUAL mismatch (not null during initialization)
            if (sessionVars?.session_user_id && sessionVars.session_user_id !== clientUserId) {
              logger.error("[useGoalFetching] CRITICAL ERROR: Session user mismatch!");
              logger.error(`Auth context: ${clientUserId}, Session user: ${sessionVars.session_user_id}`);
              toast({
                title: "Session error",
                description: "Authentication context mismatch detected. Please refresh the page.",
                variant: "destructive",
              });
              return [];
            }
          } catch (authErr) {
            logger.error("[useGoalFetching] Auth check error:", authErr);
          }
          
          // Using optimized single query approach to fetch all data efficiently
          const { data: goalsData, error } = await supabase
            .from('goals')
            .select(`
              id,
              title,
              progress,
              due_date,
              completed_at,
              celebrated_at,
              category_id,
              user_id,
              common_goal_id,
              lived_at,
              memory_caption,
              memory_location,
              categories (
                name
              ),
              todos (
                id,
                text,
                completed,
                due_date,
                subtasks (
                  id,
                  text,
                  completed,
                  due_date,
                  sub_subtasks (
                    id,
                    text,
                    completed
                  )
                )
              )
            `)
            .is('archived_at', null)
            .order('created_at', { ascending: false });

          if (error) {
            logger.error("[useGoalFetching] Supabase error:", error);
            toast({
              title: "Error loading goals",
              description: error.message,
              variant: "destructive",
            });
            return [];
          }
          
          if (!goalsData || goalsData.length === 0) {
            return [];
          }
          
          // PATCH: SILENTLY FILTER unauthorized goals, do NOT log error to console
          const validatedGoals = goalsData.filter(g => g.user_id === clientUserId);

          // Fetch shared_goals to detect gift-assigned goals
          let sharedGoalMap: Record<string, { ownerId: string; ownerName?: string }> = {};
          try {
            const { data: sharedGoals } = await supabase
              .from('shared_goals')
              .select('goal_id, owner_id')
              .eq('shared_with_id', clientUserId);
            
            if (sharedGoals && sharedGoals.length > 0) {
              const ownerIds = [...new Set(sharedGoals.map(sg => sg.owner_id))];
              const { data: profiles } = await supabase
                .from('profiles')
                .select('id, display_name')
                .in('id', ownerIds);
              
              const nameMap = new Map((profiles || []).map(p => [p.id, p.display_name || 'Someone']));
              
              for (const sg of sharedGoals) {
                sharedGoalMap[sg.goal_id] = {
                  ownerId: sg.owner_id,
                  ownerName: nameMap.get(sg.owner_id),
                };
              }
            }
          } catch (err) {
            logger.error("[useGoalFetching] Shared goals fetch error:", err);
          }

          // Transform data to match expected structure
          const processedGoals = validatedGoals.map((goal: any) => {
            const shared = sharedGoalMap[goal.id];
            return {
              id: goal.id,
              title: goal.title,
              progress: goal.progress || 0,
              dueDate: goal.due_date,
              completedAt: goal.completed_at,
              celebratedAt: goal.celebrated_at,
              category: goal.categories?.name,
              userId: goal.user_id,
              commonGoalId: goal.common_goal_id,
              sharedByUserId: shared?.ownerId,
              sharedByName: shared?.ownerName,
              livedAt: goal.lived_at,
              memoryCaption: goal.memory_caption,
              memoryLocation: goal.memory_location,
              todos: goal.todos?.map((todo: any) => ({
                id: todo.id,
                text: todo.text,
                completed: todo.completed,
                dueDate: todo.due_date,
                subtasks: todo.subtasks?.map((subtask: any) => ({
                  id: subtask.id,
                  text: subtask.text,
                  completed: subtask.completed,
                  dueDate: subtask.due_date,
                  subSubtasks: subtask.sub_subtasks?.map((subSubtask: any) => ({
                    id: subSubtask.id,
                    text: subSubtask.text,
                    completed: subSubtask.completed
                  })) || []
                })) || []
              })) || []
            };
          });
          
          return processedGoals;
        }) || []; // Fallback to empty array if withSessionSetup returns null
      } catch (err) {
        logger.error("[useGoalFetching] Unexpected error:", err);
        toast({
          title: "Error loading goals",
          description: "An unexpected error occurred while loading your goals.",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!user,
    // Optimized caching to prevent race conditions and reduce 400 errors
    staleTime: 30000,         // 30 seconds - reasonable freshness window
    gcTime: 300000,           // 5 minutes cache retention
    refetchOnMount: true,     // Normal refetch behavior
    refetchOnWindowFocus: false, // Don't refetch on focus to reduce queries
    refetchOnReconnect: true, // Refetch when network reconnects
    retry: 3,                 // Retry failed requests
    retryDelay: 1000,         // Wait 1 second between retries
  });

  return {
    goals,
    isLoading,
    error
  };
};
